/**
 * The packets waiting on the signed-in reviewer: an alert on the front door, and a count in the
 * top bar on every page — beside the presentations' own, built the same way.
 *
 * Nothing is stored to make this work. A packet sent for review is In review and assigned to its
 * reviewer, so their queue is the packets list asked for exactly that, and it empties as they
 * decide, because a decision assigns the packet back to whoever sent it.
 */
import { useState } from 'react';
import ArticleRounded from '@mui/icons-material/ArticleRounded';
import {
  Alert,
  AlertTitle,
  Badge,
  IconButton,
  Link,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { keys, packets } from '@/api/endpoints';
import { useAuth } from '@/auth/AuthProvider';
import { formatDateTime } from '@/components/common';
import type { Packet } from '@/types';

const QUEUE = { assignedToMe: true, reviewStatus: 'In review', pageSize: 50 } as const;

/** Where a packet waiting on review opens: its Review step, with Approve and Reject on it. */
export function packetReviewLink(packet: Packet): string {
  return `/packets/${packet.id}?step=review`;
}

/** The packets waiting on this user's review; none, without asking, for anyone who does not review. */
export function usePacketReviewQueue(): Packet[] {
  const { can } = useAuth();
  const reviews = can('packet:review');
  const query = useQuery({
    queryKey: keys.packets(QUEUE),
    queryFn: () => packets.list({ ...QUEUE }),
    enabled: reviews,
    refetchInterval: 120_000,
  });
  return reviews ? (query.data?.items ?? []) : [];
}

function waiting(count: number): string {
  return count === 1 ? '1 packet is waiting for your review' : `${count} packets are waiting for your review`;
}

function sentBy(packet: Packet): string {
  return `${packet.clientName ?? 'Employer'} · sent by ${packet.submittedByName ?? 'someone no longer listed'} ${formatDateTime(packet.submittedAt)}`;
}

export function PacketReviewQueueAlert(): JSX.Element | null {
  const waitingPackets = usePacketReviewQueue();
  if (waitingPackets.length === 0) return null;
  return (
    <Alert severity="info" icon={<ArticleRounded />}>
      <AlertTitle>{waiting(waitingPackets.length)}</AlertTitle>
      <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
        {waitingPackets.map((packet) => (
          <li key={packet.id}>
            <Link component={RouterLink} to={packetReviewLink(packet)} sx={{ fontWeight: 600 }}>
              {packet.name}
            </Link>{' '}
            — {sentBy(packet)}
          </li>
        ))}
      </Stack>
    </Alert>
  );
}

export function PacketReviewQueueButton(): JSX.Element | null {
  const waitingPackets = usePacketReviewQueue();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (waitingPackets.length === 0) return null;
  return (
    <>
      <Tooltip title={waiting(waitingPackets.length)}>
        <IconButton aria-label={waiting(waitingPackets.length)} onClick={(event) => setAnchor(event.currentTarget)}>
          <Badge badgeContent={waitingPackets.length} color="primary">
            <ArticleRounded />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {waitingPackets.map((packet) => (
          <MenuItem
            key={packet.id}
            onClick={() => {
              setAnchor(null);
              navigate(packetReviewLink(packet));
            }}
          >
            <ListItemText primary={packet.name} secondary={sentBy(packet)} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
